import { Box, Typography, Avatar } from '@mui/material';

/**
 * ConversationList component renders a single conversation item with
 * user avatar, username, message preview, and message date.
 * It supports a click handler for item interaction.
 *
 * Props:
 * - userName: string - Name of the conversation user
 * - message: string - Last message preview text
 * - date: string - Date/time string for the message
 * - avatar: string - URL for the user's avatar image
 * - onClick: function - Callback for click event on the conversation item
 */
const ConversationList = ({ userName, message, date, avatar, onClick }) => {
  return (
    // Container box for entire conversation list item
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', // Horizontal layout: avatar + text
        alignItems: 'center', // Vertically center items
        padding: '10px',
        cursor: 'pointer', // Pointer cursor to indicate clickability
        '&:hover': { backgroundColor: '#f0f0f0' }, // Hover highlight
        boxSizing: 'border-box', // Include padding in width calculations
        height: '80px', // Fixed height for uniformity
        overflow: 'hidden', // Prevent content spillover
        // backgroundColor: '#fff', // White background
        mb: '10px'
      }}
    >
      {/* Avatar container */}
      <Box>
        <Avatar
          src={avatar}
          sx={{
            marginRight: '10px', // Space between avatar and text
          }}
        />
      </Box>

      {/* Text container: username, date, and message */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column', // Stack username/date and message vertically
          flexGrow: 1, // Take remaining horizontal space
          minWidth: 0, // Allow shrinking below content width to enable ellipsis
          overflow: 'hidden', // Prevent overflow of text content
        }}
      >
        {/* Username and date container - horizontally spaced */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between', // Username left, date right
            alignItems: 'center',
            overflow: 'hidden', // Prevent overflow
          }}
        >
          {/* Username text */}
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              overflow: 'hidden',
              textOverflow: 'ellipsis', // Truncate with ellipsis if too long
              whiteSpace: 'nowrap', // Single line text
            }}
          >
            {userName}
          </Typography>

          {/* Date text */}
          <Typography variant="caption" color="textSecondary">
            {date}
          </Typography>
        </Box>

        {/* Message preview text */}
        <Box
          sx={{
            overflow: 'hidden', // Prevent overflow in message preview
            textAlign: 'left',
          }}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis', // Truncate message if too long
              whiteSpace: 'nowrap', // Single line text
            }}
          >
            {message}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationList;
